import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react'
import type { AuthUser, LoginPayload, ChangePasswordPayload } from '@/services/auth/types'
import * as authClient from '@/services/auth/authClient'

export interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  error: string | null
  login: (payload: LoginPayload) => Promise<AuthUser>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  changePassword: (payload: ChangePasswordPayload) => Promise<void>
  can: (permission: string | string[]) => boolean
  hasRole: (role: string | string[]) => boolean
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refreshUser = useCallback(async () => {
    try {
      const u = await authClient.getMe()
      setUser(u)
      setError(null)
    } catch (err: any) {
      setUser(null)
      setError(err?.message || 'Lỗi phiên đăng nhập')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshUser()
  }, [refreshUser])

  const login = useCallback(async (payload: LoginPayload) => {
    const res = await authClient.login(payload)
    setUser(res.user)
    return res.user
  }, [])

  const logout = useCallback(async () => {
    try {
      await authClient.logout()
    } finally {
      setUser(null)
    }
  }, [])

  const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
    await authClient.changePassword(payload)
    await refreshUser()
  }, [refreshUser])

  const can = useCallback(
    (permission: string | string[]): boolean => {
      if (!user) return false
      // If user is required to change password, deny business permissions until changed
      if (user.mustChangePassword) return false

      const required = Array.isArray(permission) ? permission : [permission]
      const userPerms = new Set(user.permissions ?? [])
      const hasFullWildcard = userPerms.has('*')
      const hasReadWildcard = userPerms.has('*.read')

      return required.some((req) => {
        if (hasFullWildcard) return true
        if (userPerms.has(req)) return true
        if (req === '*.read' && hasReadWildcard) return true
        if (req.endsWith('.read') && hasReadWildcard) return true
        return false
      })
    },
    [user],
  )

  const hasRole = useCallback(
    (role: string | string[]): boolean => {
      if (!user) return false
      const required = Array.isArray(role) ? role : [role]
      const userRoles = new Set(user.roles ?? [])
      return required.some((r) => userRoles.has(r))
    },
    [user],
  )

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
      refreshUser,
      changePassword,
      can,
      hasRole,
    }),
    [user, loading, error, login, logout, refreshUser, changePassword, can, hasRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}

export function useCan(permission: string | string[]): boolean {
  const { can } = useAuth()
  return can(permission)
}

export function useRole(role: string | string[]): boolean {
  const { hasRole } = useAuth()
  return hasRole(role)
}
